import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Select,
  MenuItem,
  InputAdornment,
  Fade,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Paper,
  Popper,
} from '@mui/material';
import {
  Close,
  Description,
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Search,
  AccessTime,
  PersonOutline,
} from '@mui/icons-material';
import { colors } from './Followup.styles';
import { searchLeadsApi } from '../../../api/followupTasks.api';
import { createFollowupTaskApi } from '../../../api/followupTasks.api';
import { getAllUsers } from '../../../api/users.api';

const DAYS_OF_WEEK = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const taskTypes = ['Call', 'Email', 'Meeting', 'Follow-up', 'Demo', 'Proposal'];

const CreateTaskModal = ({ open, onClose, onSubmit, preSelectedLead = null }) => {
  const today = new Date();
  const [taskType, setTaskType] = useState('Call');
  const [clientSearch, setClientSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [description, setDescription] = useState('');
  const [telecaller, setTelecaller] = useState('');
  const [telecallers, setTelecallers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(today);
  const [preferredTime, setPreferredTime] = useState('10:00 AM');
  const [priority, setPriority] = useState('low');
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  // Lead search states
  const [leadResults, setLeadResults] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Task creation states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingTelecallers, setLoadingTelecallers] = useState(false);

  // Fetch telecallers when modal opens and set pre-selected lead
  useEffect(() => {
    if (open) {
      fetchTelecallers();
      // If preSelectedLead is provided, set it
      if (preSelectedLead) {
        setSelectedLead(preSelectedLead);
        setClientSearch(preSelectedLead.custom_fields?.full_name || preSelectedLead.client_name);
      }
    }
  }, [open, preSelectedLead]);

  // Handle lead search with debouncing
  useEffect(() => {
    if (clientSearch.trim().length >= 2) {
      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {
        searchLeads(clientSearch);
      }, 300); // 300ms debounce
    } else {
      setLeadResults([]);
      setShowDropdown(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [clientSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchTelecallers = async () => {
    try {
      setLoadingTelecallers(true);
      // Fetch all active users as potential telecallers
      const response = await getAllUsers();
      const users = response.data?.data || [];
      // Filter only active users
      const activeUsers = users.filter(user => user.status === 'active');
      setTelecallers(activeUsers);
    } catch (error) {
      console.error('Error fetching telecallers:', error);
      setTelecallers([]);
    } finally {
      setLoadingTelecallers(false);
    }
  };

  const searchLeads = async (query) => {
    try {
      setLoadingLeads(true);
      const response = await searchLeadsApi(query);
      setLeadResults(response.data?.leads || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error searching leads:', error);
      setLeadResults([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleLeadSelect = (lead) => {
    setSelectedLead(lead);
    setClientSearch(lead.custom_fields?.full_name || lead.client_name);
    setShowDropdown(false);
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, disabled: true });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      days.push({
        day,
        date,
        isToday: date.toDateString() === today.toDateString(),
        isSelected: selectedDate && date.toDateString() === selectedDate.toDateString(),
        disabled: false,
      });
    }

    return days;
  }, [currentMonth, currentYear, selectedDate]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDayClick = (dayInfo) => {
    if (dayInfo.disabled || !dayInfo.date) return;
    setSelectedDate(dayInfo.date);
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedLead) {
      alert('Please select a lead from the search results');
      return;
    }

    if (!taskType || !selectedDate || !preferredTime) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setIsSubmitting(true);

      // Find selected telecaller info
      const selectedTelecaller = telecallers.find(t => t._id === telecaller);

      const taskData = {
        taskType,
        leadId: selectedLead._id,
        leadName: selectedLead.client_name,
        description,
        assignedTo: telecaller || null,
        assignedToName: selectedTelecaller?.name || 'Unassigned',
        scheduledDate: selectedDate.toISOString(),
        preferredTime,
        priority,
      };

      await createFollowupTaskApi(taskData);

      // Call parent onSubmit if provided
      if (onSubmit) {
        onSubmit(taskData);
      }

      handleClose();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTaskType('Call');
    setClientSearch('');
    setSelectedLead(null);
    setDescription('');
    setTelecaller('');
    setSelectedDate(today);
    setPreferredTime('10:00 AM');
    setPriority('low');
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    setLeadResults([]);
    setShowDropdown(false);
    setIsSubmitting(false);
    onClose();
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '14px',
      bgcolor: '#fff',
      '& fieldset': {
        borderColor: '#E2E8F0',
      },
      '&:hover fieldset': {
        borderColor: '#CBD5E1',
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.primary,
      },
    },
  };

  const selectStyles = {
    borderRadius: '8px',
    fontSize: '14px',
    bgcolor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E2E8F0',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#CBD5E1',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary,
    },
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            outline: 'none',
            width: 720,
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              borderBottom: '1px solid #E2E8F0',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '20px', color: '#111827' }}>
                Create New Task
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#6B7280', mt: 0.5 }}>
                Fill in the details to schedule a new follow-up.
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                color: '#9CA3AF',
                '&:hover': { bgcolor: '#F3F4F6', color: '#6B7280' },
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ display: 'flex', p: 3 }}>
            {/* Left Side - Task Details */}
            <Box sx={{ flex: 1, pr: 3, borderRight: '1px solid #E2E8F0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Description sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'primary.main',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Task Details
                </Typography>
              </Box>

              {/* Task Type */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 1 }}>
                  Task Type
                </Typography>
                <Select
                  value={taskType}
                  onChange={(e) => setTaskType(e.target.value)}
                  fullWidth
                  size="small"
                  sx={selectStyles}
                >
                  {taskTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Lead / Client */}
              <Box sx={{ mb: 3, position: 'relative' }} ref={searchInputRef}>
                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 1 }}>
                  Lead / Client
                </Typography>
                <TextField
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search client name..."
                  fullWidth
                  size="small"
                  disabled={!!preSelectedLead}
                  sx={inputStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                    endAdornment: loadingLeads && !preSelectedLead && (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Dropdown for search results */}
                {showDropdown && !preSelectedLead && (
                  <Paper
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      mt: 0.5,
                      maxHeight: 200,
                      overflow: 'auto',
                      zIndex: 1300,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      borderRadius: '8px',
                    }}
                  >
                    {leadResults.length > 0 ? (
                      <List sx={{ p: 0 }}>
                        {leadResults.map((lead) => (
                          <ListItem
                            key={lead._id}
                            onClick={() => handleLeadSelect(lead)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#F3F4F6' },
                              borderBottom: '1px solid #E2E8F0',
                              '&:last-child': { borderBottom: 'none' },
                            }}
                          >
                            <ListItemText
                              primary={lead.custom_fields?.full_name || lead.client_name}
                              secondary={lead.contact}
                              primaryTypographyProps={{
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#374151',
                              }}
                              secondaryTypographyProps={{
                                fontSize: '12px',
                                color: '#6B7280',
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                          No leads found
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}
              </Box>

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 1 }}>
                  Description
                </Typography>
                <TextField
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add specific task details here..."
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  sx={inputStyles}
                />
              </Box>

              {/* Assign To Telecaller */}
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 1 }}>
                  Assign To Telecaller
                </Typography>
                <Select
                  value={telecaller}
                  onChange={(e) => setTelecaller(e.target.value)}
                  fullWidth
                  size="small"
                  sx={selectStyles}
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <PersonOutline sx={{ fontSize: 18, color: '#9CA3AF' }} />
                    </InputAdornment>
                  }
                  disabled={loadingTelecallers}
                >
                  <MenuItem value="" sx={{ color: '#9CA3AF' }}>
                    Unassigned
                  </MenuItem>
                  {telecallers.map((caller) => (
                    <MenuItem
                      key={caller._id}
                      value={caller._id}
                      sx={{ color: '#374151' }}
                    >
                      {caller.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            {/* Right Side - Scheduling & Priority */}
            <Box sx={{ flex: 1, pl: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CalendarMonth sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'primary.main',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Scheduling & Priority
                </Typography>
              </Box>

              {/* Calendar */}
              <Box
                sx={{
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                  p: 2,
                  mb: 3,
                  bgcolor: '#fff',
                }}
              >
                {/* Calendar Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <IconButton onClick={handlePrevMonth} sx={{ width: 28, height: 28, color: '#9CA3AF' }}>
                    <ChevronLeft fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}>
                    {MONTHS[currentMonth]} {currentYear}
                  </Typography>
                  <IconButton onClick={handleNextMonth} sx={{ width: 28, height: 28, color: '#9CA3AF' }}>
                    <ChevronRight fontSize="small" />
                  </IconButton>
                </Box>

                {/* Week Days */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                  {DAYS_OF_WEEK.map((day, index) => (
                    <Typography
                      key={index}
                      sx={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', textAlign: 'center', py: 1 }}
                    >
                      {day}
                    </Typography>
                  ))}
                </Box>

                {/* Days Grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                  {calendarDays.map((dayInfo, index) => (
                    <Box
                      key={index}
                      onClick={() => handleDayClick(dayInfo)}
                      sx={{
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: dayInfo.isSelected ? 600 : 400,
                        color: dayInfo.disabled
                          ? '#CBD5E1'
                          : dayInfo.isSelected
                            ? '#fff'
                            : '#374151',
                        bgcolor: dayInfo.isSelected
                          ? 'primary.main'
                          : dayInfo.isToday
                            ? '#F3E8FF'
                            : 'transparent',
                        cursor: dayInfo.disabled ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: dayInfo.disabled
                            ? 'transparent'
                            : dayInfo.isSelected
                              ? 'primary.main'
                              : '#F1F5F9',
                        },
                      }}
                    >
                      {dayInfo.day}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Preferred Time & Priority Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Preferred Time */}
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 1 }}>
                    Preferred Time
                  </Typography>
                  <TextField
                    value={preferredTime}
                    onChange={(e) => setPreferredTime(e.target.value)}
                    fullWidth
                    size="small"
                    sx={inputStyles}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <AccessTime sx={{ fontSize: 18, color: '#9CA3AF' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Priority */}
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 1 }}>
                    Priority
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      bgcolor: '#fff',
                    }}
                  >
                    {['low', 'med', 'high'].map((level) => (
                      <Box
                        key={level}
                        component="button"
                        onClick={() => setPriority(level)}
                        sx={{
                          flex: 1,
                          py: 1,
                          px: 1.5,
                          fontSize: '12px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: priority === level
                            ? level === 'high' ? '#DC2626' : level === 'med' ? '#D97706' : '#374151'
                            : '#9CA3AF',
                          bgcolor: priority === level
                            ? level === 'high' ? '#FEE2E2' : level === 'med' ? '#FEF3C7' : '#F1F5F9'
                            : 'transparent',
                          border: 'none',
                          borderRight: level !== 'high' ? '1px solid #E2E8F0' : 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: priority === level
                              ? undefined
                              : '#F8FAFC',
                          },
                        }}
                      >
                        {level.toUpperCase()}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              p: 3,
              borderTop: '1px solid #E2E8F0',
              bgcolor: '#F8FAFC',
            }}
          >
            <Button
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                color: '#6B7280',
                fontWeight: 500,
                px: 3,
                '&:hover': { bgcolor: '#F3F4F6' },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedLead}
              sx={{
                textTransform: 'none',
                bgcolor: 'primary.main',
                fontWeight: 600,
                px: 4,
                py: 1.25,
                borderRadius: '8px',
                boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}4D`,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: (theme) => `0 6px 16px ${theme.palette.primary.main}66`,
                },
                '&:disabled': {
                  bgcolor: '#CBD5E1',
                  color: '#9CA3AF',
                },
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                  Creating...
                </>
              ) : (
                'Create Task'
              )}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CreateTaskModal;
